import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGetProductsQuery } from '../store/apiSlice';
import { PriceDisplay, Button, SizePicker, OptimizedImage, HorizontalScroll } from '../components/ui';
import { useCartActions } from '../hooks/useCartActions';
import { describePromoPrice } from '../store/cartSlice';
import {
  ArrowLeft,
  ShoppingBag,
  Tag,
  Sparkles,
  Check,
  Loader2,
  FileText,
  AlertTriangle
} from 'lucide-react';

export default function DetalleProducto() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCartFn, buyNow, isAdded } = useCartActions();

  const { data: productsData, isLoading, isError } = useGetProductsQuery({});
  const products = productsData?.items ?? [];
  const product = products.find(p => p.id === id);

  const [selectedSize, setSelectedSize] = useState<string>('Único');
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  useEffect(() => {
    if (product) {
      const sizesList = product.sizes ? product.sizes.split(', ').filter(Boolean) : [];
      if (sizesList.length > 0) setSelectedSize(sizesList[0]);
    }
  }, [product]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-white overflow-hidden">
        <div className="flex-1 flex flex-col justify-center items-center pt-[81px] bg-slate-50/30">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
          <p className="text-slate-500 text-xs mt-4 font-semibold">Cargando detalles del producto...</p>
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="flex flex-col h-screen bg-white overflow-hidden">
        <div className="flex-1 flex flex-col justify-center items-center pt-[81px] px-6 max-w-md mx-auto text-center space-y-5">
          <div className="bg-rose-50 p-4 rounded-2xl border border-rose-500/10 text-rose-650 flex items-center justify-center">
            <AlertTriangle className="w-10 h-10" />
          </div>
          <div className="space-y-2">
            <h3 className="font-heading font-extrabold text-xl text-slate-800">Producto No Encontrado</h3>
            <p className="text-slate-500 text-xs leading-relaxed">
              Lo sentimos, no pudimos encontrar el producto que estás buscando. Es posible que haya sido eliminado o que el enlace sea incorrecto.
            </p>
          </div>
          <Button variant="primary" onClick={() => navigate('/catalogo')} leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Volver al Catálogo
          </Button>
        </div>
      </div>
    );
  }

  // Gallery images
  const images = (product.images && product.images.length > 0)
    ? product.images
    : [product.image, product.imageSecondary].filter(Boolean) as string[];

  // Sizes array
  const sizesList = product.sizes ? product.sizes.split(', ').filter(Boolean) : [];

  // Calculate discount
  const { price: displayPrice, originalPrice: displayOriginal, percentOff, caption: priceCaption } = describePromoPrice(product);
  const discountPercent = percentOff ?? (displayOriginal && displayOriginal > displayPrice
    ? Math.round(((displayOriginal - displayPrice) / displayOriginal) * 100)
    : null);

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      <div className="flex-1 overflow-y-auto pt-[81px]">
      <main className="max-w-7xl mx-auto w-full px-6 lg:px-16 py-8 md:py-12 space-y-8">

        {/* Back navigation */}
        <div className="relative z-10">
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-blue-500 transition-colors uppercase tracking-wider group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            Volver al Catálogo
          </Link>
        </div>

        {/* Split Details view */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start relative z-10">
          
          {/* Column 1: Image Gallery Showcase */}
          <div className="lg:col-span-7 flex flex-col gap-4 w-full">
            <div className="relative aspect-square w-full bg-slate-50 border border-slate-200/60 rounded-3xl overflow-hidden select-none flex items-center justify-center shadow-sm">
              <OptimizedImage
                src={images[activeImageIndex]}
                alt={product.name}
                displayWidth={1200}
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="w-full h-full object-cover transition-opacity duration-300"
              />
              
              {/* Dynamic discount floating badge */}
              {discountPercent && (
                <span className="absolute top-4 right-4 bg-gradient-to-r from-rose-600 to-rose-500 text-white text-[10px] font-extrabold px-3 py-1.5 rounded-full shadow-lg border border-white/20 uppercase tracking-wider animate-pulse">
                  -{discountPercent}% OFF
                </span>
              )}

              {/* Status Badge */}
              {product.badge && (
                <span className="absolute top-4 left-4 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500/90 text-white text-[10px] font-extrabold px-3 py-1.5 rounded-full shadow-lg border border-white/20 uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" />
                  {product.badge}
                </span>
              )}
            </div>

            {/* Thumbnail Navigation Row */}
            {images.length > 1 && (
              <HorizontalScroll gap="gap-3" className="py-1">
                {images.map((img, idx) => {
                  const isActive = activeImageIndex === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-20 h-20 rounded-2xl overflow-hidden border-2 bg-white transition-all flex-shrink-0 relative focus:outline-none ${
                        isActive
                          ? 'border-blue-500 shadow-md shadow-blue-500/10 scale-102'
                          : 'border-slate-200 hover:border-slate-350'
                      }`}
                    >
                      <img src={img} alt="Miniatura" className="w-full h-full object-cover" />
                      {!isActive && <div className="absolute inset-0 bg-white/30 hover:opacity-0 transition-opacity" />}
                    </button>
                  );
                })}
              </HorizontalScroll>
            )}
          </div>

          {/* Column 2: Product Specifications and Checkout options */}
          <div className="lg:col-span-5 space-y-6 w-full">
            <div className="space-y-4">
              {/* Category Tag */}
              <span className="inline-flex items-center gap-1.5 text-[9px] font-extrabold bg-blue-50 border border-blue-500/10 text-blue-500 px-3 py-1.5 rounded-full uppercase tracking-wider select-none">
                <Tag className="w-2.5 h-2.5" />
                {product.category}
              </span>

              {/* Title & Description */}
              <div className="space-y-2">
                <h1 className="font-heading font-extrabold text-slate-800 text-2xl md:text-3xl leading-tight">
                  {product.name}
                </h1>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                  {product.description}
                </p>
              </div>
            </div>

            {/* Promotion Callout */}
            {product.promotion && (
              <div className="bg-emerald-50/70 border border-emerald-500/20 p-4 rounded-2xl space-y-1.5 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider">
                    {product.promotion.label}
                  </span>
                  <span className="font-heading text-xs font-bold text-emerald-800 uppercase tracking-wide">
                    ¡Promoción Especial!
                  </span>
                </div>
                <p className="text-[11px] text-emerald-700 leading-relaxed font-medium">
                  {product.promotion.name}
                  {product.promotion.description && (
                    <span className="block text-[10px] text-emerald-600 font-normal mt-0.5">
                      {product.promotion.description}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Pricing Section */}
            <div className="bg-slate-50 border border-slate-200/60 p-5 rounded-2xl flex items-center justify-between shadow-sm">
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider">Precio Final</span>
                <div className="flex items-center gap-2.5 mt-0.5">
                  <PriceDisplay price={displayPrice} originalPrice={displayOriginal} size="lg" />
                </div>
                {priceCaption && (
                  <span className="text-xs text-emerald-600 font-bold mt-1">{priceCaption}</span>
                )}
              </div>
            </div>

            {/* Size Picker selector */}
            {sizesList.length > 0 && (
              <div className="space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-slate-500 font-extrabold uppercase tracking-wider">
                    Elegí tu Talle
                  </span>
                  <span className="text-xs text-blue-500 font-extrabold bg-blue-50 border border-blue-500/10 px-2.5 py-0.5 rounded-lg">
                    Talle {selectedSize}
                  </span>
                </div>
                <SizePicker
                  sizes={sizesList}
                  selected={selectedSize}
                  onSelect={setSelectedSize}
                  variant="pills"
                />
              </div>
            )}

            {/* Ficha técnica accordion-like block */}
            {product.characteristics && (
              <div className="space-y-3 pt-4 border-t border-slate-150/50">
                <span className="text-[10px] text-slate-500 font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-blue-500" /> Características Técnicas
                </span>
                <div className="bg-slate-50/50 border border-slate-200/60 p-4 rounded-2xl">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-[10px] text-slate-650 leading-relaxed">
                    {product.characteristics.split('; ').map((spec, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0" />
                        <span>{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Checkout & Add to Cart action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-slate-150/50">
              <Button
                variant="primary"
                onClick={() => product && buyNow(product, selectedSize)}
                disabled={false}
                className="flex-1 py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 group/btn text-xs font-bold uppercase tracking-wider animate-pulse hover:animate-none"
                leftIcon={<ShoppingBag className="w-4 h-4 text-white" />}
              >
                Comprar ahora
              </Button>

              <Button
                variant="outline"
                onClick={() => product && addToCartFn(product, selectedSize)}
                disabled={false}
                className={`flex-1 py-4 rounded-xl border flex items-center justify-center gap-2 transition-all duration-300 text-xs font-bold uppercase tracking-wider ${
                  isAdded(product.id)
                    ? '!border-emerald-400 !bg-emerald-50 !text-emerald-700 hover:!bg-emerald-100'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900'
                }`}
              >
                {isAdded(product.id) ? (
                  <><Check className="w-4 h-4 text-emerald-600" /> ¡Agregado!</>
                ) : (
                  'Agregar al carrito'
                )}
              </Button>
            </div>
          </div>

        </div>
      </main>

      <footer className="bg-[#f3f0e9] border-t border-slate-200/80 py-8 px-6 lg:px-16 text-center text-xs text-slate-500 space-y-3">
        <p>© 2026 Aye Otero Calzados. Todos los derechos reservados.</p>
        <p>Belgrano 422, Capitán Sarmiento, Buenos Aires. Envíos a todo el país.</p>
      </footer>
      </div>
    </div>
  );
}
